import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { NotificationDTO } from '@em/shared';
import { api, useMessages } from '../../api';
import { Badge, Button, Card, EmptyState, PageHeader, QueryState } from '../../components/ui';

/** Where a message leads, from the ids the server attached to it. */
function linkFor(n: NotificationDTO): { to: string; label: string } | null {
  const d = (n.data ?? {}) as Record<string, string>;
  if (d.correctionId) return { to: '/corrections', label: 'Open corrections' };
  if (d.payPeriodId) return { to: `/periods/${d.payPeriodId}`, label: 'Open pay period' };
  if (d.deviceId || n.kind === 'sync_health' || n.kind === 'kiosk_lockout' || n.kind === 'device_pending') return { to: '/settings/kiosks', label: 'Open devices' };
  return null;
}

export function MessagesPage() {
  const q = useMessages();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['messages'] });
  const unread = q.data?.filter((n) => !n.readAt).length ?? 0;
  return (
    <>
      <PageHeader
        title="Messages"
        subtitle="Approval deadlines, corrections sent to you, kiosk alerts and the daily device check."
        actions={unread > 0 ? <Button onClick={async () => (await api.readAllUserNotifications(), refresh())}>Mark all read</Button> : null}
      />
      <QueryState q={q}>
        {(items) =>
          !items.length ? (
            <Card>
              <EmptyState title="No messages">Reminders and alerts for you appear here.</EmptyState>
            </Card>
          ) : (
            <Card tight>
              <ul className="messages">
                {items.map((n) => {
                  const link = linkFor(n);
                  return (
                    <li key={n.id} className={n.readAt ? '' : 'unread'}>
                      <div className="row spread">
                        <strong>{n.title}</strong>
                        <span className="muted small">{new Date(n.createdAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                      </div>
                      <div className="pre">{n.body}</div>
                      <div className="row">
                        {!n.readAt && <Badge tone="amber">New</Badge>}
                        {link && (
                          <Link to={link.to} onClick={() => !n.readAt && void api.readUserNotification(n.id).then(refresh)}>
                            {link.label}
                          </Link>
                        )}
                        {!n.readAt && (
                          <Button size="sm" variant="ghost" onClick={async () => (await api.readUserNotification(n.id), refresh())}>
                            Mark read
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )
        }
      </QueryState>
    </>
  );
}
